// Billing-related constants shared across billing pages. Kept here (rather than
// colocated with a single page component) so two independently-routed pages can
// read the same data without one importing from the other.

// Part number → human-readable description. Used by the "Part Number
// Description" KPI card on both the team-scoped deployments view
// (`TeamPartNumberTracking`) and the company-wide deployments view
// (`CompanyPartNumberDeployments`). Consumers fall back to "-" when a
// part number isn't in this map (e.g. a newly-introduced SKU that the
// page hasn't been taught about yet).
export const PART_NUMBER_DESCRIPTIONS = {
  'PA-VTE-CORE-BASE-001':
    'Virtual Test Environment (VTE), Platform base fee that covers maintenance and support of the platform service. Monthly Subscription, Per Airline',
  'PA-VTE-VLRU-S-001':
    'Virtual LRU Small, 4 CPU Cores, 8 GB of RAM, 150 GB storage included. Per virtual LRU per Month per VTE',
  'PA-VTE-VLRU-M-001':
    'Virtual LRU Medium, 8 CPU Cores, 32 GB of RAM, 150 GB storage included. Per virtual LRU per Month per VTE',
  'PA-VTE-VLRU-L-001':
    'Virtual LRU Large, 12 CPU Cores, 32 GB of RAM, 150 GB storage included. Per virtual LRU per Month per VTE',
  'PA-VTE-VLRU-X-001':
    'Virtual LRU X-Large, 16 CPU Cores, 128 GB of RAM, 150 GB storage included. Per virtual LRU per Month per VTE',
  'PA-VTE-MEDIA-S-001':
    'Virtual LRU Storage Expansion, Media Tier Small, per 250 GB of storage. Per LRU per month per VTE',
  'PA-VTE-MEDIA-M-001':
    'Virtual LRU Storage Expansion, Media Tier Medium, per 500 GB of storage. Per LRU per month per VTE',
  'PA-VTE-MEDIA-L-001':
    'Virtual LRU Storage Expansion, Media Tier Large, per 1 TB of storage. Per LRU per Month per VTE',
  'PA-VTE-VLRU-C-YYYY':
    'Custom Virtual LRU, Virtual LRUs that deviate from standard profiles. Requires custom quote based on customer requirements. Per virtual LRU per Month per VTE'
};

// --- Deployment treatment + table shared between billing pages ---
//
// The team-scoped deployments view (`TeamPartNumberTracking`) and the
// company-wide deployments view (`CompanyPartNumberDeployments`) render the
// same treatment buckets with the same chip styling and the same column
// order. Keep those rules in one place so a new bucket, a column rename, or
// a date-format tweak only has to land in one file.

// Buckets the API deployment can fall into. Order matters for `deriveTreatment`:
// `billable_quantity === 0` wins over decommissioned (a deployment with no
// remaining billable quantity has been *replaced*, regardless of its
// decommission date).
export const TREATMENTS = {
  BILLABLE_ACTIVE: 'Billable - Active',
  BILLABLE_DELETED: 'Billable - Deleted',
  EXCLUDED_REPLACED: 'Excluded - Replaced'
};

// Maps the derived treatment buckets to MUI Chip colors. Add a new entry
// here when introducing a new bucket — the chip will pick it up
// automatically.
export const TREATMENT_COLORS = {
  [TREATMENTS.BILLABLE_ACTIVE]: { color: 'primary', variant: 'outlined' },
  [TREATMENTS.BILLABLE_DELETED]: { color: 'warning', variant: 'outlined' },
  [TREATMENTS.EXCLUDED_REPLACED]: { color: 'default', variant: 'outlined' }
};

// Format an ISO date (or any Date-parseable value) for the deployments
// table. Falls back to "N/A" for null/undefined so missing `provision_date`
// or `decommission_date` values render cleanly.
//
// The API emits dates as UTC ISO strings (e.g. "2026-08-29T03:11:11.626Z"),
// so we parse them as UTC and format the calendar-day + 12h time in UTC
// (e.g. "Aug 29, 2026 3:11 AM"). Rendering in local time would shift
// late-UTC dates into the previous local day — "2026-08-29T03:11:11Z" is
// still Aug 29 in UTC, but it lands on Aug 28 in anything west of UTC.
// The "Z" suffix is the API's promise that the value IS UTC, so the
// display should match that promise.
//
// Bare day strings ("2026-08-29") have no time component — they format
// with `12:00 AM` to keep the column width stable while making it clear
// the time wasn't recorded.
export function formatDate(value) {
  if (!value) return 'N/A';
  if (typeof value === 'string') {
    const dayMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dayMatch) {
      const [, year, month, day] = dayMatch;
      return formatDateParts(Number(year), Number(month), Number(day), 0, 0);
    }
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
    if (isoMatch) {
      const [, year, month, day, hour, minute] = isoMatch;
      return formatDateParts(
        Number(year),
        Number(month),
        Number(day),
        Number(hour),
        Number(minute)
      );
    }
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return formatDateParts(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes()
  );
}

// Render the (year, month, day, hour, minute) tuple as a string in
// 12-hour "h:mm AM/PM" form (matching the Vrack Table's Created Date
// column). Hour 0 renders as `12:00 AM`, hour 12 as `12:00 PM`, and
// hours 1-11 as `1:00 AM` through `11:00 AM` / `1:00 PM`-`11:00 PM`.
// No leading zero on the hour so the format stays aligned with the
// Vrack Table's `MM/dd/yyyy p` date-fns output.
function formatDateParts(year, month, day, hour, minute) {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];
  const period = hour < 12 ? 'AM' : 'PM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const mm = String(minute).padStart(2, '0');
  return `${monthNames[month - 1]} ${day}, ${year} ${hour12}:${mm} ${period}`;
}

// Normalize a Date or ISO string into the local-calendar 'YYYY-MM-DD'
// day string. The API returns dates as either bare day strings
// ('YYYY-MM-DD') or full ISO datetimes ('YYYY-MM-DDTHH:MM:SS.sssZ');
// comparing them as plain strings would treat the shorter prefix as
// "less than" the longer one and silently exclude any deployment
// whose provision_date matches the active day exactly. Splitting the
// day off the front here means every comparison goes day-vs-day.
export function toIsoDay(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Derive the treatment bucket from the deployment fields the API actually
// returns. The old mock data had an explicit `treatment` string; the API
// gives us the pieces (`billable_quantity`, `decommission_date`) instead.
export function deriveTreatment(deployment) {
  const billable = deployment.billable_quantity ?? 0;
  const decommissioned = deployment.decommission_date != null;
  if (billable === 0) return TREATMENTS.EXCLUDED_REPLACED;
  if (decommissioned) return TREATMENTS.BILLABLE_DELETED;
  return TREATMENTS.BILLABLE_ACTIVE;
}

// Map a single API deployment into the shared UI row shape used by both
// deployment tables. Returns only the fields every page renders; callers
// (e.g. the company-wide view, which threads `teamName` through every row)
// spread additional fields on top.
export function toBaseDeploymentRow(deployment) {
  return {
    id: `${deployment.vrack_name}-${deployment.provision_date}`,
    vrackId: deployment.vrack_name,
    quantity: deployment.quantity ?? 0,
    billableQuantity: deployment.billable_quantity ?? 0,
    treatment: deriveTreatment(deployment),
    owner: deployment.owner,
    createdDate: deployment.provision_date,
    deletedDate: deployment.decommission_date
  };
}

// --- Peak concurrent quantity ---
//
// "Peak concurrent quantity" answers the question: at any moment in the
// month, what was the maximum number of units of this part number that
// were simultaneously deployed? This is computed from the *deployment*
// level (each deployment contributes its `quantity` for the half-open
// interval `[provision_date, decommission_date)`), NOT from the
// `billable_quantity` field — `billable_quantity` reflects billing
// rules and can drop to 0 on a replaced rack even while the rack is
// still deployed. Using `billable_quantity` would understate the
// actual peak.
//
// Sweep-line semantics:
//   * Create a `+quantity` event at `provision_date`.
//   * Create a `-quantity` event at `decommission_date`.
//   * Sort all events chronologically; ties go to the `-` event first
//     so `[start, end)` half-open semantics are preserved (a
//     deployment decommissioned at the same instant another one is
//     provisioned contributes to neither timestamp).
//   * Walk the timeline, maintaining a running concurrent total.
//   * The peak is the max running total observed at any event in the
//     requested month.
//
// Day bucketing (for the per-day chart bars and the day-by-day peak
// tracking) decomposes the same computation into per-day windows. A
// deployment contributes its `quantity` to day D iff the
// `[provision_date, decommission_date)` interval overlaps the day
// window `[D 00:00:00 UTC, D+1 00:00:00 UTC)`.

// Parse an API date string as a UTC instant. The API emits either
// bare day strings ('YYYY-MM-DD') or full ISO datetimes
// ('YYYY-MM-DDTHH:MM:SS.sssZ'). Both should resolve to a UTC moment
// so day-bucket math is timezone-stable.
//
// A bare 'YYYY-MM-DD' is treated as the *start* of that UTC day
// (00:00:00Z) and tagged as `isDayOnly: true`. Callers that want
// inclusive-day semantics (e.g. "alive for the entire day D when
// both provision and decommission are date-only on day D") must
// read `isDayOnly` and treat the day-only end as
// `start + 24 hours`, not as the bare day start. Otherwise a bare
// 'YYYY-MM-DD' decommission would collapse `[start, end)` to a
// zero-length interval and the deployment would never appear on
// any day.
function parseDateAsUtc(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    const dayMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dayMatch) {
      const [, year, month, day] = dayMatch;
      return {
        instant: new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))),
        isDayOnly: true
      };
    }
    const isoMatch =
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/.exec(
        value
      );
    if (isoMatch) {
      const [, year, month, day, hour, minute, second] = isoMatch;
      return {
        instant: new Date(
          Date.UTC(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour),
            Number(minute),
            Number(second)
          )
        ),
        isDayOnly: false
      };
    }
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return { instant: d, isDayOnly: false };
}

// First day of the calendar month containing `month`, as a UTC Date.
function firstDayOfMonthUtc(month) {
  return new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), 1));
}

// Last day of the calendar month containing `month`, as a UTC Date.
function lastDayOfMonthUtc(month) {
  return new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0));
}

// ISO 'YYYY-MM-DD' for a UTC Date.
function toUtcIsoDay(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Build the inclusive list of every UTC day in `month`.
function eachDayInMonthUtc(month) {
  const days = [];
  const start = firstDayOfMonthUtc(month);
  const end = lastDayOfMonthUtc(month);
  for (
    let cursor = new Date(start);
    cursor.getTime() <= end.getTime();
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  ) {
    days.push(toUtcIsoDay(cursor));
  }
  return days;
}

// True when the deployment's `[provision_date, decommission_date)`
// half-open window overlaps the UTC day window starting at `dayIso`.
// Returns false for any deployment missing a provision_date (it was
// never alive on any day).
//
// Date-only semantics: when both the provision and decommission are
// bare 'YYYY-MM-DD' strings on the same day, the deployment is
// considered alive for the full day D (inclusive-of-day semantics
// for the rendered table). Without this rule, a date-only
// decommission would parse to the same instant as the provision
// (both at 00:00 of day D) and the half-open window would collapse
// to zero, hiding the deployment from the day's table.
export function wasActiveOnDay(provisionDate, decommissionDate, dayIso) {
  const provision = parseDateAsUtc(provisionDate);
  if (!provision) return false;
  const provisionTime = provision.instant.getTime();
  const dayStart = new Date(`${dayIso}T00:00:00.000Z`);
  const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
  // Overlap iff start < dayEnd AND (end is null OR end > dayStart).
  // Half-open: a decommission at exactly dayStart counts as "not alive
  // during day D" because the rack went away before any moment of D.
  if (provisionTime >= dayEnd.getTime()) return false;
  if (!decommissionDate) return true;
  const decommission = parseDateAsUtc(decommissionDate);
  if (!decommission) return true; // unparseable end => assume alive
  const decommissionTime = decommission.instant.getTime();
  // If both dates are bare 'YYYY-MM-DD' strings, the decommission
  // day is the *end* of that day (start of next day) so the rack
  // was alive for the entire rendered day. This keeps single-day
  // deployments (created and deleted on the same day) visible.
  const decommissionEnd =
    provision.isDayOnly && decommission.isDayOnly
      ? decommissionTime + 24 * 60 * 60 * 1000
      : decommissionTime;
  return decommissionEnd > dayStart.getTime();
}

// True when the deployment was alive at the given instant. Used by
// the row-highlight predicate to filter deployments to those that
// contributed to the bar's peak instant (not just to the day's
// overlap set -- those two sets can differ on busy days).
//
// Half-open `[start, end)` semantics: a deployment is alive at the
// instant iff `provision_time <= instant < decommission_time` (or
// no decommission_time and `provision_time <= instant`).
//
// Date-only exception: when both dates are bare 'YYYY-MM-DD', the
// deployment is alive for the entire day (start-of-day through
// end-of-day), so any instant on that day reads as "alive".
export function wasAliveAtInstant(provisionDate, decommissionDate, instantMsArg) {
  const provision = parseDateAsUtc(provisionDate);
  if (!provision) return false;
  const provisionTime = provision.instant.getTime();
  if (provisionTime > instantMsArg) return false;
  if (decommissionDate == null) return true;
  const decommission = parseDateAsUtc(decommissionDate);
  if (!decommission) return true; // unparseable end => assume alive
  const decommissionTime = decommission.instant.getTime();
  const decommissionEnd =
    provision.isDayOnly && decommission.isDayOnly
      ? decommissionTime + 24 * 60 * 60 * 1000
      : decommissionTime;
  return decommissionEnd > instantMsArg;
}

/**
 * Compute the peak concurrent quantity of a part number for the
 * requested billing month, returning per-day maxima and the monthly
 * peak + the calendar day(s) on which the peak was reached.
 *
 * Algorithm -- one global sweep-line over the month, bucketed per
 * UTC day:
 *   1. Generate the full inclusive list of UTC days in the month.
 *   2. For each deployment that overlaps the month (provision_date
 *      < monthEnd AND (decommission_date is null OR
 *      decommission_date > monthStart)), clamp its `[provision_date,
 *      decommission_date)` half-open interval to the day window
 *      `[dayStart, dayEnd)` and emit a `+quantity` event at
 *      `max(provision_date, dayStart)` and a `-quantity` event at
 *      `min(decommission_date, dayEnd)`.
 *   3. Sort events chronologically; ties go to the `-` event first so
 *      `[start, end)` semantics are preserved (a deployment
 *      decommissioned at the same instant another one is provisioned
 *      contributes to neither).
 *   4. Initialize each day's running total to the sum of `quantity`
 *      for deployments alive at the start of the day window (those
 *      whose clamped interval starts at `dayStart`).
 *   5. Walk the events in order; track the max running total
 *      observed inside each day window. That max IS the day's bar
 *      height.
 *   6. Monthly peak = max of the daily maxima. Peak days are ALL
 *      days that hit the monthly peak (ties are preserved).
 *
 * Why a sweep-line instead of "sum overlapping deployments per day":
 * the user wanted *peak* concurrent units -- the most racks alive at
 * any single instant -- not the daily integration of overlapping
 * racks. A naive daily sum over-counts when many short-lived
 * deployments overlap (e.g. several test racks provisioned and
 * decommissioned within the same day never actually share a moment
 * in time, but a daily sum would still add them all up). The
 * sweep-line walks the actual event timeline so concurrent counts
 * only ever reflect racks that were alive at the same instant.
 *
 * Returns `null` when `month` is null so callers can render an empty
 * state without separately checking the MonthPicker.
 *
 * @param {Array<{quantity?: number, billableQuantity?: number,
 *   createdDate?: string, deletedDate?: string}>} deployments
 *   The page-level deployment rows (already mapped to the UI shape).
 * @param {Date|null} month First day of the selected billing month
 *   (any Date inside the month works; we use UTC month boundaries).
 * @returns {{days: string[], totals: number[],
 *   monthlyPeak: number, peakDays: string[],
 *   peakInstants: (number|null)[],
 *   isFutureDays: boolean[]} | null}
 */
export function computePeakConcurrentByDay(deployments, month) {
  if (!month) return null;
  const days = eachDayInMonthUtc(month);
  const monthStart = firstDayOfMonthUtc(month).getTime();
  const monthEnd = lastDayOfMonthUtc(month).getTime() + 24 * 60 * 60 * 1000;

  // Pre-filter deployments that overlap the month at all. A
  // deployment provisioned and decommissioned entirely outside the
  // month never contributes to any day in the month.
  const overlapping = [];
  deployments.forEach((d) => {
    const qty = d.quantity || 0;
    if (qty === 0) return;
    const provision = parseDateAsUtc(d.createdDate);
    if (!provision) return;
    const provisionTime = provision.instant.getTime();
    if (provisionTime >= monthEnd) return; // not provisioned yet
    const decommission = parseDateAsUtc(d.deletedDate);
    // Date-only decommission on day D: treat the end as the START
    // of day D+1 (24h after start of D). Without this rule, a
    // date-only provision + date-only decommission on the same day
    // would parse to identical instants and the deployment would
    // be invisible for that day.
    let decommissionTime = decommission ? decommission.instant.getTime() : null;
    if (
      decommissionTime !== null &&
      provision.isDayOnly &&
      decommission &&
      decommission.isDayOnly
    ) {
      decommissionTime += 24 * 60 * 60 * 1000;
    }
    if (decommissionTime !== null && decommissionTime <= monthStart) return; // gone before month
    overlapping.push({
      qty,
      provisionTime,
      decommissionTime
    });
  });

  // Per-day running total at the start of the day window. A
  // deployment contributes to this iff it was alive at dayStart:
  //   * provisionTime <= dayStart (provisioned on or before dayStart;
  //     a provision exactly at dayStart counts as "alive at
  //     dayStart" -- half-open `[start, end)` semantics mean the
  //     rack was alive for at least one instant of the day).
  //   * decommissionTime is null OR decommissionTime > dayStart
  //     (a decomm exactly at dayStart counts as "gone before any
  //     moment of the day").
  const initialTotals = new Array(days.length).fill(0);
  overlapping.forEach((d) => {
    days.forEach((dayIso, dayIdx) => {
      const dayStart = new Date(`${dayIso}T00:00:00.000Z`).getTime();
      if (
        d.provisionTime <= dayStart &&
        (d.decommissionTime === null || d.decommissionTime > dayStart)
      ) {
        initialTotals[dayIdx] += d.qty;
      }
    });
  });

  // Build the per-day events. A deployment emits `+qty` at
  // max(provision, dayStart) iff that's strictly inside the day
  // window (otherwise the event is "at the day boundary" and
  // already handled by initialTotals). Same for `-qty` at
  // min(decommission, dayEnd) iff < dayEnd. These are the only
  // instants where the running total changes during the day.
  const eventsByDay = days.map(() => []);
  overlapping.forEach((d) => {
    days.forEach((dayIso, dayIdx) => {
      const dayStart = new Date(`${dayIso}T00:00:00.000Z`).getTime();
      const dayEnd = dayStart + 24 * 60 * 60 * 1000;
      if (d.provisionTime > dayStart && d.provisionTime < dayEnd) {
        eventsByDay[dayIdx].push({ time: d.provisionTime, delta: +d.qty });
      }
      if (
        d.decommissionTime !== null &&
        d.decommissionTime > dayStart &&
        d.decommissionTime < dayEnd
      ) {
        eventsByDay[dayIdx].push({ time: d.decommissionTime, delta: -d.qty });
      }
    });
  });

  // Walk each day's events; track running total + max within the
  // day. Ties on time go to `-` first so a decomm-then-prov at the
  // same instant collapses to "neither alive" within the day.
  // Also track the instant the day's max was first reached --
  // `peakInstant`. The row highlight uses this single instant so
  // clicking a bar highlights exactly the racks alive at the bar's
  // peak (and only those, not every rack that overlapped the day
  // window -- a day's bar is its peak, not its daily overlap sum).
  const totals = new Array(days.length).fill(0);
  const peakInstants = new Array(days.length).fill(null);
  days.forEach((dayIso, dayIdx) => {
    const events = eventsByDay[dayIdx];
    events.sort((a, b) => a.time - b.time || a.delta - b.delta);
    const dayStart = new Date(`${dayIso}T00:00:00.000Z`).getTime();
    let running = initialTotals[dayIdx];
    let max = running;
    // If the day starts already at the max (e.g. all deployments
    // alive at dayStart), the peak instant is the start of the day.
    peakInstants[dayIdx] = max > 0 ? dayStart : null;
    events.forEach((ev) => {
      running += ev.delta;
      if (running > max) {
        max = running;
        peakInstants[dayIdx] = ev.time;
      }
    });
    totals[dayIdx] = max;
  });

  let monthlyPeak = 0;
  totals.forEach((t) => {
    if (t > monthlyPeak) monthlyPeak = t;
  });
  const peakDays = [];
  totals.forEach((t, i) => {
    if (t === monthlyPeak && monthlyPeak > 0) peakDays.push(days[i]);
  });

  // Compute "is this day in the future?" once. Future days are days
  // whose UTC start is strictly after the viewer's current moment.
  // These days always render as a zero-height bar regardless of
  // ongoing deployments -- the user can't see future data, and the
  // peak shouldn't be dragged up by an unfinished month. We expose
  // `isFutureDays` so the chart can both visually distinguish
  // future bars and disable clicks on them.
  const nowMs = Date.now();
  const isFutureDays = days.map((iso) => {
    const dayStart = new Date(`${iso}T00:00:00.000Z`).getTime();
    return dayStart > nowMs;
  });
  isFutureDays.forEach((isFuture, idx) => {
    if (isFuture) {
      totals[idx] = 0;
      peakInstants[idx] = null;
    }
  });

  // Recompute monthlyPeak and peakDays after zeroing future days.
  monthlyPeak = 0;
  totals.forEach((t) => {
    if (t > monthlyPeak) monthlyPeak = t;
  });
  peakDays.length = 0;
  totals.forEach((t, i) => {
    if (t === monthlyPeak && monthlyPeak > 0) peakDays.push(days[i]);
  });

  return { days, totals, monthlyPeak, peakDays, peakInstants, isFutureDays };
}

// Shared columns for the deployments table. The company-wide view prepends
// a `team` column; the team-scoped view uses these verbatim. Order here is
// the column order in the rendered table.
export const DEPLOYMENT_TABLE_COLUMNS = [
  { id: 'vrack', label: 'VRack', alignRight: false },
  { id: 'quantity', label: 'Quantity', alignRight: false },
  { id: 'owner', label: 'Owner', alignRight: false },
  { id: 'createdDate', label: 'Created Date', alignRight: false },
  { id: 'deletedDate', label: 'Deleted Date', alignRight: false }
];
